/**
 * 画像管理コンポーネント
 * 画像情報の一覧表示と編集、Markdownへの挿入機能を提供
 */

import React, { useCallback, useEffect, useState } from "react";
import "./ImageManager.css";
import { ImageInfo, ImageUploader } from "./ImageUploader";

// ImageInfoを再エクスポート
export type { ImageInfo };

interface ImageManagerProps {
  images: ImageInfo[];
  onImagesChange: (images: ImageInfo[]) => void;
  onInsertImage: (keyName: string) => void;
  title?: string;
  acceptTypes?: string; // "image/*" | "video/*" | "image/*,video/*"
}

export const ImageManager: React.FC<ImageManagerProps> = ({
  images,
  onImagesChange,
  onInsertImage,
  title = "画像管理",
  acceptTypes = "image/*",
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingImage, setEditingImage] = useState<ImageInfo | null>(null);

  // コンテンツ画像の場合は常に編集モード
  const isContentImages = title === "コンテンツ画像";

  // Admin用画像プロキシURLを生成（署名不要）
  const generateImageUrl = useCallback((storagePath: string): string | null => {
    if (!storagePath) return null;
    // Admin用画像プロキシエンドポイントを使用
    return `/api/admin/image/${storagePath}`;
  }, []);

  // 画像が追加されたときにプレビューURLを自動生成
  useEffect(() => {
    const loadPreviewUrls = () => {
      // ローカルファイルの場合はスキップ（既にpreviewUrlが設定されている）
      const imagesNeedingPreview = images.filter(
        (img) => img.objectName && !img.previewUrl && !img.file
      );

      if (imagesNeedingPreview.length === 0) {
        return; // プレビューURLが必要な画像がない場合は何もしない
      }

      console.log(
        `[ImageManager] ${imagesNeedingPreview.length}件の画像のプレビューURLを生成中`
      );
      const updatedImages = [...images];
      let hasChanges = false;

      for (let i = 0; i < updatedImages.length; i++) {
        const image = updatedImages[i];
        if (image.objectName && !image.previewUrl && !image.file) {
          console.log(
            `[ImageManager] プレビューURLを生成中: ${image.objectName}`
          );
          const imageUrl = generateImageUrl(image.objectName);
          if (imageUrl) {
            updatedImages[i] = { ...image, previewUrl: imageUrl };
            hasChanges = true;
            console.log(`[ImageManager] プレビューURL生成成功: ${image.name}`);
          } else {
            console.warn(
              `[ImageManager] プレビューURL生成失敗: ${image.objectName}`
            );
          }
        }
      }

      if (hasChanges) {
        console.log(`[ImageManager] 画像を更新: ${hasChanges}件の変更`);
        onImagesChange(updatedImages);
      }
    };

    loadPreviewUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    images.length,
    images.map((img) => img.objectName).join(","),
    images.map((img) => img.previewUrl || "").join(","),
  ]); // 画像数、objectName、previewUrlの状態が変わったとき実行

  // プレビューURLを更新
  const updatePreviewUrl = (index: number) => {
    const image = images[index];
    if (!image.objectName) return;

    const imageUrl = generateImageUrl(image.objectName);
    if (imageUrl) {
      const updatedImages = [...images];
      updatedImages[index] = { ...updatedImages[index], previewUrl: imageUrl };
      onImagesChange(updatedImages);
    }
  };

  const handleUploadComplete = (imageInfo: ImageInfo) => {
    console.log(`[ImageManager] 画像追加:`, imageInfo);

    // ローカルファイルの場合は既にpreviewUrlが設定されている
    // objectNameがある場合はプロキシURLを生成（編集モードで既にアップロード済みの場合）
    if (imageInfo.objectName && !imageInfo.previewUrl && !imageInfo.file) {
      console.log(
        `[ImageManager] プレビューURLを生成中: ${imageInfo.objectName}`
      );
      const imageUrl = generateImageUrl(imageInfo.objectName);
      if (imageUrl) {
        imageInfo.previewUrl = imageUrl;
        console.log(`[ImageManager] プレビューURL生成成功`);
      } else {
        console.warn(`[ImageManager] プレビューURL生成失敗`);
      }
    }

    // タイトル画像の場合は1つだけ、コンテンツ画像の場合は複数可能
    if (title === "タイトル画像" || title === "タイトル画像をアップロード") {
      onImagesChange([imageInfo]);
    } else {
      onImagesChange([...images, imageInfo]);
    }
  };

  const handleDelete = (index: number) => {
    if (confirm("この画像を削除しますか？")) {
      const updatedImages = images.filter((_, i) => i !== index);
      onImagesChange(updatedImages);
    }
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditingImage({ ...images[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex === null || !editingImage) return;

    const updatedImages = [...images];
    updatedImages[editingIndex] = editingImage;
    onImagesChange(updatedImages);
    setEditingIndex(null);
    setEditingImage(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingImage(null);
  };

  // コンテンツ画像の編集フィールド変更ハンドラ
  const handleContentImageChange = (
    index: number,
    field: keyof ImageInfo,
    value: any
  ) => {
    const updatedImages = [...images];
    updatedImages[index] = { ...updatedImages[index], [field]: value };
    onImagesChange(updatedImages);
  };

  const handleInsertImage = (keyName: string) => {
    onInsertImage(keyName);
  };

  return (
    <div className="image-manager">
      <div className="image-manager-header">
        <h3 className="image-manager-title">{title}</h3>
        <ImageUploader
          onUploadComplete={handleUploadComplete}
          onError={(error) => alert(error)}
          accept={acceptTypes}
        />
      </div>

      <div className="image-list">
        {images.length === 0 ? (
          <div className="image-empty">
            {acceptTypes === "video/*"
              ? "動画がありません"
              : acceptTypes === "image/*,video/*"
              ? "メディアがありません"
              : "画像がありません"}
          </div>
        ) : (
          images.map((image, index) => (
            <div key={index} className="image-item">
              <div className="image-preview">
                {image.previewUrl ? (
                  image.mediaType === "video" ? (
                    <video
                      src={image.previewUrl}
                      controls
                      style={{ maxWidth: "100%", maxHeight: "100%" }}
                      onError={async () => {
                        if (image.file) {
                          console.warn(
                            `[ImageManager] ローカル動画読み込みエラー: ${image.name}`
                          );
                          return;
                        }
                        if (image.objectName) {
                          await updatePreviewUrl(index);
                        }
                      }}
                    />
                  ) : (
                    <img
                      src={image.previewUrl}
                      alt={image.name}
                      onError={async () => {
                        if (image.file) {
                          console.warn(
                            `[ImageManager] ローカル画像読み込みエラー: ${image.name}`
                          );
                          return;
                        }
                        if (image.objectName) {
                          await updatePreviewUrl(index);
                        }
                      }}
                      onLoad={() => {
                        console.log(
                          `[ImageManager] 画像読み込み成功: ${image.name}`
                        );
                      }}
                    />
                  )
                ) : image.objectName ? (
                  <div className="image-loading">
                    <div
                      style={{
                        marginBottom: "8px",
                        color: "#6b7280",
                        fontSize: "12px",
                      }}
                    >
                      プレビュー読み込み中...
                    </div>
                    <button
                      type="button"
                      onClick={() => updatePreviewUrl(index)}
                      className="btn-load-preview"
                    >
                      プレビューを読み込む
                    </button>
                  </div>
                ) : image.file ? (
                  <div className="image-loading">
                    <div style={{ color: "#6b7280", fontSize: "12px" }}>
                      プレビュー準備中...
                    </div>
                  </div>
                ) : (
                  <div className="image-placeholder">
                    <div
                      style={{
                        color: "#9ca3af",
                        fontSize: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      画像なし
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="btn-delete-overlay"
                  onClick={() => handleDelete(index)}
                  title="画像を削除"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18"></path>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                  </svg>
                </button>
              </div>

              {isContentImages || editingIndex === index ? (
                <div className="image-edit-form">
                  <div className="form-field">
                    <label>画像名</label>
                    <input
                      type="text"
                      value={
                        isContentImages ? image.name : editingImage?.name || ""
                      }
                      onChange={(e) => {
                        if (isContentImages) {
                          handleContentImageChange(
                            index,
                            "name",
                            e.target.value
                          );
                        } else {
                          setEditingImage({
                            ...editingImage!,
                            name: e.target.value,
                          });
                        }
                      }}
                      onBlur={(e) => {
                        // 入力終了時に確実に更新
                        if (isContentImages) {
                          handleContentImageChange(
                            index,
                            "name",
                            e.target.value
                          );
                        } else if (editingImage) {
                          const updatedImages = [...images];
                          updatedImages[index] = {
                            ...editingImage,
                            name: e.target.value,
                          };
                          onImagesChange(updatedImages);
                        }
                      }}
                    />
                  </div>
                  <div className="form-field">
                    <label>キー名（Markdownで使用）</label>
                    <input
                      type="text"
                      value={
                        isContentImages
                          ? image.keyName
                          : editingImage?.keyName || ""
                      }
                      onChange={(e) => {
                        if (isContentImages) {
                          handleContentImageChange(
                            index,
                            "keyName",
                            e.target.value
                          );
                        } else {
                          setEditingImage({
                            ...editingImage!,
                            keyName: e.target.value,
                          });
                        }
                      }}
                      onBlur={(e) => {
                        // 入力終了時に確実に更新
                        if (isContentImages) {
                          handleContentImageChange(
                            index,
                            "keyName",
                            e.target.value
                          );
                        } else if (editingImage) {
                          const updatedImages = [...images];
                          updatedImages[index] = {
                            ...editingImage,
                            keyName: e.target.value,
                          };
                          onImagesChange(updatedImages);
                        }
                      }}
                      placeholder="例: image1"
                    />
                  </div>
                  <div className="form-field">
                    <label>サイズ</label>
                    <div className="size-input-group">
                      <input
                        type="number"
                        value={
                          isContentImages ? image.size : editingImage?.size || 0
                        }
                        onChange={(e) => {
                          if (isContentImages) {
                            handleContentImageChange(
                              index,
                              "size",
                              Number(e.target.value)
                            );
                          } else {
                            setEditingImage({
                              ...editingImage!,
                              size: Number(e.target.value),
                            });
                          }
                        }}
                      />
                      <select
                        value={
                          isContentImages
                            ? image.sizeUnit
                            : editingImage?.sizeUnit || "KB"
                        }
                        onChange={(e) => {
                          if (isContentImages) {
                            handleContentImageChange(
                              index,
                              "sizeUnit",
                              e.target.value
                            );
                          } else {
                            setEditingImage({
                              ...editingImage!,
                              sizeUnit: e.target.value,
                            });
                          }
                        }}
                      >
                        <option value="B">B</option>
                        <option value="KB">KB</option>
                        <option value="MB">MB</option>
                      </select>
                    </div>
                  </div>
                  {!isContentImages && (
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveEdit}
                      >
                        保存
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancelEdit}
                      >
                        キャンセル
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
